import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  CircularProgress,
  List,
  ListItem,
  Paper,
  Fade,
  Grid,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import MedicationIcon from '@mui/icons-material/Medication';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import { medicationsAnalyzerService, RecognizeMedicationResponse } from '../../services/medications-analyzer.service';
import { toastService } from '../../services/toast.service';

interface QuickMedicationAddModalProps {
  open: boolean;
  onClose: () => void;
  selectedDate: string; // YYYY-MM-DD
  onRefreshDashboard?: () => void;
}

export const QuickMedicationAddModal: React.FC<QuickMedicationAddModalProps> = ({ 
  open, 
  onClose, 
  selectedDate,
  onRefreshDashboard 
}) => {
  const [inputText, setInputText] = useState('');
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [recognizedEvents, setRecognizedEvents] = useState<RecognizeMedicationResponse[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleClose = () => {
    setInputText('');
    setRecognizedEvents([]);
    setIsRecognizing(false);
    setEditingIndex(null);
    onClose();
  };

  const handleRecognize = async () => {
    if (!inputText.trim()) return;

    setIsRecognizing(true);
    setEditingIndex(null);
    try {
      const now = new Date();
      const [year, month, day] = selectedDate.split('-').map(Number);
      const contextDate = new Date(year, month - 1, day, now.getHours(), now.getMinutes(), now.getSeconds());

      const data = await medicationsAnalyzerService.recognize({
        text: inputText,
        contextDate: contextDate.toISOString()
      });
      setRecognizedEvents(data.items);
    } catch (error: any) {
      console.error('Medication recognition error:', error);
      toastService.error(error?.message || 'Не вдалося розпізнати препарати. Спробуйте ще раз.');
    } finally {
      setIsRecognizing(false);
    }
  };

  const formatTime = (isoString: string) => {
    try {
      return new Date(isoString).toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  const handleUpdateEventField = (index: number, field: keyof RecognizeMedicationResponse, value: any) => {
    const updatedEvents = [...recognizedEvents];
    updatedEvents[index] = { ...updatedEvents[index], [field]: value };
    setRecognizedEvents(updatedEvents);
  };

  const handleConfirmAll = async () => {
    toastService.success('Записи про препарати успішно додано (імітація)');
    if (onRefreshDashboard) onRefreshDashboard();
    handleClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '500px',
          borderRadius: '16px',
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHighIcon sx={{ color: '#E91E63' }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Швидке додавання ліків через ШІ</Typography>
        </Box>
        <IconButton aria-label="close" onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f9f9f9', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            fullWidth
            label="Які препарати ви прийняли?"
            placeholder="Наприклад: 2 таблетки аспірину, вітамін С 500мг о 10:00"
            variant="outlined"
            multiline
            rows={recognizedEvents.length > 0 ? 3 : 6}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            disabled={isRecognizing}
            sx={{
              bgcolor: 'white',
              transition: 'all 0.3s ease',
              '& .MuiInputBase-root': {
                borderRadius: '12px',
              }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button 
              onClick={handleRecognize} 
              variant="contained" 
              disabled={isRecognizing || !inputText.trim()}
              startIcon={isRecognizing ? <CircularProgress size={20} color="inherit" /> : <AutoFixHighIcon />}
              sx={{ 
                backgroundColor: '#E91E63', 
                borderRadius: '10px',
                px: 3,
                '&:hover': { backgroundColor: '#c2185b' } 
              }}
            >
              {isRecognizing ? 'Аналізуємо...' : (recognizedEvents.length > 0 ? 'Аналізувати заново' : 'Аналізувати')}
            </Button>
          </Box>
        </Box>

        {recognizedEvents.length > 0 && (
          <Fade in={recognizedEvents.length > 0}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#282828' }}>
                Розпізнані записи:
              </Typography>
              <List disablePadding>
                {recognizedEvents.map((event, index) => (
                  <Paper 
                    key={index} 
                    variant="outlined" 
                    sx={{ 
                        mb: 1.5, 
                        borderRadius: '12px', 
                        overflow: 'hidden',
                        borderColor: editingIndex === index ? '#E91E63' : '#e0e0e0',
                        borderWidth: editingIndex === index ? 2 : 1
                    }}
                  >
                    <ListItem sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', mb: 1 }}>
                        <Box sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
                          <MedicationIcon sx={{ color: '#E91E63' }} />
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          {editingIndex === index ? (
                            <TextField 
                                fullWidth
                                size="small"
                                label="Назва препарату"
                                value={event.name}
                                onChange={(e) => handleUpdateEventField(index, 'name', e.target.value)}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                    {event.name}
                                </Typography>
                                {event.originalText && (
                                    <Tooltip title={`Оригінальний текст: "${event.originalText}"`}>
                                        <InfoIcon sx={{ fontSize: '1rem', color: '#A8A8A8', cursor: 'help' }} />
                                    </Tooltip>
                                )}
                                <Tooltip title={`Час прийому: ${formatTime(event.scheduledLdt)}`}>
                                    <AccessTimeIcon sx={{ fontSize: '1rem', color: '#A8A8A8', cursor: 'help' }} />
                                </Tooltip>
                            </Box>
                          )}
                        </Box>
                        <Button 
                          size="small" 
                          variant={editingIndex === index ? "contained" : "text"} 
                          color={editingIndex === index ? "secondary" : "inherit"}
                          onClick={() => setEditingIndex(editingIndex === index ? null : index)}
                          startIcon={editingIndex === index ? <SaveIcon /> : <EditIcon />}
                          sx={{ ml: 1 }}
                        >
                          {editingIndex === index ? 'Зберегти' : 'Змінити'}
                        </Button>
                      </Box>

                      <Grid container spacing={2} sx={{ width: '100%', ml: 4 }}>
                        <Grid item xs={6} sm={4}>
                          {editingIndex === index ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Кількість"
                              type="number"
                              value={event.quantity || ''}
                              onChange={(e) => handleUpdateEventField(index, 'quantity', e.target.value)}
                            />
                          ) : (
                            <Typography variant="body2"><b>Кількість:</b> {event.quantity || 0}</Typography>
                          )}
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          {editingIndex === index ? (
                            <TextField
                              fullWidth
                              size="small"
                              label="Од. виміру"
                              value={event.unit || ''}
                              placeholder="табл, капс, мг..."
                              onChange={(e) => handleUpdateEventField(index, 'unit', e.target.value)}
                            />
                          ) : (
                            <Typography variant="body2"><b>Од. виміру:</b> {event.unit || '-'}</Typography>
                          )}
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="body2" color="text.secondary">
                                <b>Час:</b> {formatTime(event.scheduledLdt)}
                            </Typography>
                        </Grid>
                      </Grid>
                    </ListItem>
                  </Paper>
                ))}
              </List>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: '#f9f9f9' }}>
        <Button onClick={handleClose} color="inherit">
          Скасувати
        </Button>
        <Button 
          onClick={handleConfirmAll}
          variant="contained" 
          disabled={recognizedEvents.length === 0 || isRecognizing || editingIndex !== null}
          sx={{ 
            backgroundColor: '#E91E63', 
            borderRadius: '10px',
            px: 4,
            '&:hover': { backgroundColor: '#c2185b' } 
          }}
        >
          Підтвердити та зберегти
        </Button>
      </DialogActions>
    </Dialog>
  );
};
